import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  BarChart3, 
  Send, 
  Eye, 
  Plus, 
  RefreshCw, 
  Pencil,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

interface ReportContent {
  summary: string;
  period: string;
  generated_at: string;
  metrics: {
    totalConversations: number;
    totalMessages: number;
    avgMessagesPerConversation: number;
    escalations: number;
    refusals: number;
    deflections: number;
    topTopics: Array<{ topic: string; count: number }>;
    timeSavedMinutes: number;
  };
  freshness: {
    lastConversation: string | null;
    daysSinceLastActivity: number;
  };
  highlights: string[];
  recommendations: string[];
}

interface MonthlyReport {
  id: string;
  tenant_id: string;
  month: string;
  status: string | null;
  draft_content: any;
  edited_content: any;
  recipients: string[] | null;
  sent_at: string | null;
  created_at: string | null;
  tenants?: {
    name: string;
    client_contact_email: string | null;
  };
}

const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  draft: { 
    color: 'bg-yellow-500/10 text-yellow-500', 
    icon: <Clock className="w-3 h-3" />,
    label: 'Draft'
  },
  sent: { 
    color: 'bg-green-500/10 text-green-500', 
    icon: <CheckCircle className="w-3 h-3" />,
    label: 'Sent'
  },
  send_failed: { 
    color: 'bg-red-500/10 text-red-500', 
    icon: <AlertCircle className="w-3 h-3" />,
    label: 'Failed'
  },
};

const AdminReports: React.FC = () => {
  const [reports, setReports] = useState<MonthlyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState<MonthlyReport | null>(null);
  const [editedContent, setEditedContent] = useState<ReportContent | null>(null);
  const [viewMode, setViewMode] = useState<'view' | 'edit'>('view');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchReports = async () => {
    try {
      const { data, error } = await supabase
        .from('monthly_reports')
        .select(`
          *,
          tenants:tenant_id (
            name,
            client_contact_email
          )
        `)
        .order('month', { ascending: false });

      if (error) throw error;
      setReports((data as unknown as MonthlyReport[]) || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleGenerateReports = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-monthly-reports', {
        body: {},
      });

      if (error) throw error;

      toast.success(`Generated ${data.reportsGenerated} reports for ${data.month}`);
      fetchReports();
    } catch (error: any) {
      console.error('Error generating reports:', error);
      toast.error(error.message || 'Failed to generate reports');
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenReport = (report: MonthlyReport) => {
    setSelectedReport(report);
    setEditedContent(report.edited_content || report.draft_content);
    setViewMode('view');
  };

  const handleSaveEdits = async () => {
    if (!selectedReport || !editedContent) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('monthly_reports')
        .update({
          edited_content: editedContent as any,
        })
        .eq('id', selectedReport.id);

      if (error) throw error;

      toast.success('Report saved');
      fetchReports();
      setViewMode('view');
    } catch (error: any) {
      console.error('Error saving report:', error);
      toast.error(error.message || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReport = async () => {
    if (!selectedReport) return;

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-monthly-report', {
        body: { reportId: selectedReport.id },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`Report sent to ${data.recipients.join(', ')}`);
        fetchReports();
        setSelectedReport(null);
      } else {
        throw new Error(data.error || 'Failed to send report');
      }
    } catch (error: any) {
      console.error('Error sending report:', error);
      toast.error(error.message || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const formatMonth = (dateStr: string) => {
    const date = new Date(dateStr + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const draftReports = reports.filter(r => r.status === 'draft');
  const sentReports = reports.filter(r => r.status === 'sent');

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Monthly Reports</h1>
            <p className="text-muted-foreground mt-1">
              Generate and send monthly performance reports to tenants
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchReports} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleGenerateReports} disabled={generating}>
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Generate Reports
            </Button>
          </div>
        </div>

        {/* Drafts Due */}
        {draftReports.length > 0 && (
          <Card className="border-amber-500/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Drafts Pending Review ({draftReports.length})
              </CardTitle>
              <CardDescription>
                These reports are ready for review and sending
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Conversations</TableHead>
                    <TableHead>Time Saved</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {draftReports.map((report) => {
                    const content = report.edited_content || report.draft_content;
                    return (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">
                          {formatMonth(report.month)}
                        </TableCell>
                        <TableCell>
                          {report.tenants?.name || (
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {report.tenant_id.slice(0, 8)}...
                            </code>
                          )}
                        </TableCell>
                        <TableCell>
                          {content?.metrics?.totalConversations || 0}
                        </TableCell>
                        <TableCell>
                          {content?.metrics?.timeSavedMinutes 
                            ? `${Math.round(content.metrics.timeSavedMinutes / 60 * 10) / 10}h`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {report.created_at 
                            ? new Date(report.created_at).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleOpenReport(report)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* All Reports */}
        <Card>
          <CardHeader>
            <CardTitle>All Reports</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No reports generated</h3>
                <p className="text-muted-foreground mt-1 mb-4">
                  Generate your first monthly report for all active tenants
                </p>
                <Button onClick={handleGenerateReports} disabled={generating}>
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Generate Reports
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => {
                    const status = statusConfig[report.status || 'draft'];
                    return (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">
                          {formatMonth(report.month)}
                        </TableCell>
                        <TableCell>
                          {report.tenants?.name || (
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {report.tenant_id.slice(0, 8)}...
                            </code>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary"
                            className={`${status.color} flex items-center gap-1 w-fit`}
                          >
                            {status.icon}
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {report.sent_at 
                            ? new Date(report.sent_at).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleOpenReport(report)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Report Detail Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>
                {selectedReport?.tenants?.name} - {selectedReport && formatMonth(selectedReport.month)}
              </span>
              {selectedReport?.status !== 'sent' && (
                <div className="flex gap-2">
                  {viewMode === 'view' ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setViewMode('edit')}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleSaveEdits}
                      disabled={saving}
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-1" />
                      )}
                      Save
                    </Button>
                  )}
                </div>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedReport?.status === 'sent' 
                ? `Sent on ${selectedReport.sent_at ? new Date(selectedReport.sent_at).toLocaleString() : 'unknown'}`
                : 'Review and edit the report before sending'}
            </DialogDescription>
          </DialogHeader>

          {editedContent && (
            <div className="space-y-6 py-4">
              {/* Metrics Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-primary">
                      {editedContent.metrics.totalConversations}
                    </div>
                    <div className="text-xs text-muted-foreground">Conversations</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {Math.round(editedContent.metrics.timeSavedMinutes / 60 * 10) / 10}h
                    </div>
                    <div className="text-xs text-muted-foreground">Time Saved</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-violet-600 dark:text-violet-400">
                      {editedContent.metrics.deflections}
                    </div>
                    <div className="text-xs text-muted-foreground">Deflections</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {editedContent.metrics.escalations}
                    </div>
                    <div className="text-xs text-muted-foreground">Escalations</div>
                  </CardContent>
                </Card>
              </div>

              {/* Highlights */}
              <div className="space-y-2">
                <Label>Highlights</Label>
                {viewMode === 'edit' ? (
                  <Textarea
                    value={editedContent.highlights.join('\n')}
                    onChange={(e) => setEditedContent({
                      ...editedContent,
                      highlights: e.target.value.split('\n').filter(h => h.trim()),
                    })}
                    rows={4}
                    placeholder="One highlight per line..."
                  />
                ) : (
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    {editedContent.highlights.map((h, i) => (
                      <li key={i}>{h}</li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Top Topics */}
              <div className="space-y-2">
                <Label>Top Topics</Label>
                <div className="flex flex-wrap gap-2">
                  {editedContent.metrics.topTopics.slice(0, 5).map((topic, i) => (
                    <Badge key={i} variant="secondary">
                      {topic.topic} ({topic.count})
                    </Badge>
                  ))}
                  {editedContent.metrics.topTopics.length === 0 && (
                    <span className="text-sm text-muted-foreground">No topics recorded</span>
                  )}
                </div>
              </div>

              {/* Recommendations */}
              <div className="space-y-2">
                <Label>Recommendations</Label>
                {viewMode === 'edit' ? (
                  <Textarea
                    value={editedContent.recommendations.join('\n')}
                    onChange={(e) => setEditedContent({
                      ...editedContent,
                      recommendations: e.target.value.split('\n').filter(r => r.trim()),
                    })}
                    rows={4}
                    placeholder="One recommendation per line..."
                  />
                ) : (
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    {editedContent.recommendations.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Recipients */}
              <div className="space-y-2">
                <Label>Recipients</Label>
                <Input
                  value={selectedReport?.tenants?.client_contact_email || selectedReport?.recipients?.join(', ') || ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  Recipients are configured in the tenant settings
                </p>
              </div>

              {/* Send Button */}
              {selectedReport?.status !== 'sent' && (
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={handleSendReport} 
                    disabled={sending}
                    className="w-full sm:w-auto"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Report
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminReports;
